import { useEffect, useState } from "react";

type Api = <T>(path: string, options?: { signal?: AbortSignal }) => Promise<T>;
type Snapshot<T> = {
  api: Api;
  scope: string;
  data: T | null;
  checkedAt: number | null;
  loading: boolean;
  error: string;
};
const empty = <T,>(scope: string, api: Api): Snapshot<T> => ({
  api,
  scope,
  data: null,
  checkedAt: null,
  loading: true,
  error: "",
});

export function useVoicemailMetadata<T>(
  api: Api,
  path: string,
  parse: (value: unknown) => T,
  refresh: number,
  readOnly: boolean,
  refreshVersion = 0,
) {
  const scope = `${path}:${readOnly}`;
  const [snapshot, setSnapshot] = useState<Snapshot<T>>(() =>
    empty(scope, api),
  );
  useEffect(() => {
    let disposed = false;
    let pending: AbortController | null = null;
    let timeout: ReturnType<typeof setTimeout> | undefined;
    const load = async () => {
      if (disposed || pending) return;
      const controller = new AbortController();
      pending = controller;
      setSnapshot((previous) => ({
        ...(previous.scope === scope && previous.api === api
          ? previous
          : empty<T>(scope, api)),
        loading: true,
      }));
      let cancel = () => {};
      try {
        const interrupted = new Promise<never>((_resolve, reject) => {
          cancel = () =>
            reject(
              controller.signal.reason ??
                new Error("Voicemail check canceled."),
            );
          controller.signal.addEventListener("abort", cancel, { once: true });
          timeout = setTimeout(() => {
            controller.abort(
              new Error("Voicemail did not respond. Retrying automatically."),
            );
          }, 18_000);
        });
        const raw = await Promise.race([
          Promise.resolve().then(() => {
            if (disposed || controller.signal.aborted)
              throw new Error("Voicemail check canceled.");
            return api<unknown>(path, { signal: controller.signal });
          }),
          interrupted,
        ]);
        if (disposed || controller.signal.aborted) return;
        const data = parse(raw);
        setSnapshot({
          api,
          scope,
          data,
          checkedAt: Date.now(),
          loading: false,
          error: "",
        });
      } catch (cause) {
        if (!disposed)
          setSnapshot((previous) => ({
            ...(previous.scope === scope && previous.api === api
              ? previous
              : empty<T>(scope, api)),
            loading: false,
            error:
              cause instanceof Error
                ? cause.message
                : "Voicemail could not refresh.",
          }));
      } finally {
        clearTimeout(timeout);
        controller.signal.removeEventListener("abort", cancel);
        if (pending === controller) pending = null;
      }
    };
    const whenVisible = () => {
      if (document.visibilityState === "visible") void load();
    };
    void load();
    const interval = setInterval(whenVisible, 30_000);
    document.addEventListener("visibilitychange", whenVisible);
    window.addEventListener("focus", whenVisible);
    return () => {
      disposed = true;
      pending?.abort();
      clearTimeout(timeout);
      clearInterval(interval);
      document.removeEventListener("visibilitychange", whenVisible);
      window.removeEventListener("focus", whenVisible);
    };
  }, [api, path, parse, refresh, refreshVersion, scope]);
  return snapshot.scope === scope && snapshot.api === api
    ? snapshot
    : empty<T>(scope, api);
}

export function VoicemailMetadataStatus({
  checkedAt,
  loading,
  error,
}: {
  checkedAt: number | null;
  loading: boolean;
  error: string;
}) {
  const age = checkedAt === null ? null : Date.now() - checkedAt;
  const stale = age !== null && (age < -5_000 || age > 60_000);
  return (
    <>
      {checkedAt !== null && (
        <p className="cr-form-note">
          Last checked {new Date(checkedAt).toLocaleString()}.
          {error || stale
            ? " Showing the last successful check; these messages may have changed."
            : loading
              ? " Checking for new messages…"
              : " Refreshes every 30 seconds while this page is visible."}
        </p>
      )}
      {error && (
        <p className="cr-notice error" role="alert">
          {error}
        </p>
      )}
    </>
  );
}
